"use client";

import React from "react";
import { MapPin, Clock, Car, Map as MapIcon, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
export default function FindKatbalou() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const mapReveal = useRevealOnScroll({
    threshold: 0.1,
    delay: 100
  } as any);
  const gridReveal = useRevealOnScroll({
    threshold: 0.1,
    delay: 200
  } as any);
  const address = "De Zanden 51, 7395 PA Teuge Nabij Apeldoorn";
  const hoursString = "Maandag: Gesloten; Dinsdag: 09:00 - 18:00; Woensdag: 09:00 - 21:00; Donderdag: 09:00 - 21:00; Vrijdag: 09:00 - 18:00; Zaterdag: 08:30 - 17:00; Zondag: Gesloten";
  const hoursList = hoursString.split("; ");
  const infoCards = [{
    title: "Openingstijden",
    icon: Clock,
    content: <ul className="space-y-2 text-muted-foreground">
          {hoursList.map((hour, i) => <li key={i} data-index={i} className="flex justify-between items-center border-b border-border/50 pb-2 last:border-0 last:pb-0">
              <span className="text-sm">{hour.split(": ")[0]}</span>
              <span className="text-foreground font-medium text-sm">{hour.split(": ")[1]}</span>
            </li>)}
        </ul>
  }, {
    title: "Parkeren & Locatie",
    icon: Car,
    content: <div className="space-y-4 text-muted-foreground">
          <p className="text-sm">
            U kunt gratis en gemakkelijk parkeren direct voor de ingang van onze salon in Teuge.
          </p>
          <p className="text-sm">
            Onze locatie is uitstekend bereikbaar en volledig toegankelijk voor mindervaliden. Wij verwelkomen u graag in onze moderne salon.
          </p>
        </div>
  }, {
    title: "Contact & Route",
    icon: MapIcon,
    content: <div className="space-y-6 text-muted-foreground flex flex-col h-full">
          <p className="text-sm">
            Gevestigd in het hart van Teuge. Onze salon combineert technische perfectie met een warme, persoonlijke sfeer.
          </p>
          <address className="not-italic font-medium text-foreground border-l-2 border-primary pl-4 py-1 text-sm">
            {address}<br />
            055 5222413<br />
            info@vipcompany.nl
          </address>
          <div className="mt-auto pt-4">
            <Button asChild variant="outline" className="w-full group border-primary/20 hover:bg-accent hover:text-accent-foreground rounded-lg">
              <Link to="https://maps.google.com" newTab>
                <Navigation className="mr-2 size-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                Plan uw route
              </Link>
            </Button>
          </div>
        </div>
  }];
  return <section data-section-id="149316" id="kom-langs-in-teuge" className="bg-background text-foreground py-24 px-6 md:px-12 lg:px-24 overflow-hidden">
      <div className="max-w-7xl mx-auto space-y-16">
        
        {}
        <div ref={headerReveal.ref} className={`text-center space-y-4 transition-all duration-1000 ${headerReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-4xl md:text-5xl font-serif tracking-tight font-bold">Kom Langs in Teuge</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Bezoek onze salon voor een moment van pure ontspanning en vakmanschap.
          </p>
        </div>

        {}
        <div ref={mapReveal.ref} className={`relative w-full h-[400px] md:h-[500px] rounded-lg border border-border shadow-md overflow-hidden group transition-all duration-1000 ${mapReveal.isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          {}
          <div className="absolute inset-0 bg-muted">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1620688/img/katbalou_location_map_grayscale.png" alt="Locatie van VIP Company in Teuge" className="w-full h-full object-cover grayscale opacity-60 mix-blend-multiply transition-transform duration-10000 group-hover:scale-105" />
            {}
            <div className="absolute inset-0 bg-background/10 pointer-events-none"></div>
          </div>

          {}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center animate-bounce" style={{
          animationDuration: '3s'
        }}>
            <div className="bg-primary text-primary-foreground p-3 rounded-full shadow-2xl shadow-black/20 mb-2 relative">
              <MapPin className="size-8" strokeWidth={1.5} />
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[10px] border-t-primary"></div>
            </div>
            <div className="w-12 h-3 bg-black/20 rounded-[100%] blur-sm mt-1"></div>
          </div>
        </div>

        {}
        <div ref={gridReveal.ref} className={`grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-1000 ${gridReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {infoCards.map((card, index) => {
          const Icon = card.icon;
          return <Card key={index} data-index={index} className="bg-card border-border shadow-sm hover:shadow-md transition-all duration-300 rounded-lg">
                <CardHeader className="pb-4">
                  <div className="size-12 rounded-full bg-accent flex items-center justify-center mb-4 border border-border">
                    <Icon className="size-5 text-foreground" />
                  </div>
                  <CardTitle className="text-xl font-serif font-bold tracking-wide">{card.title}</CardTitle>
                </CardHeader>
                <CardContent className="h-[calc(100%-7rem)]">
                  {card.content}
                </CardContent>
              </Card>;
        })}
        </div>

      </div>
    </section>;
}